<template>
  <div v-if="state" class="consult-card" :class="{ booked: state.booking }">
    <template v-if="state.booking">
      <div class="icon done">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path d="M5 12l5 5L20 7" />
        </svg>
      </div>
      <div class="body">
        <div class="label">Author Nation LIVE · Booth A7</div>
        <div class="title">You're booked: {{ shortSlotLabel(state.booking.startsAt) }} (Las Vegas time)</div>
      </div>
      <div class="actions">
        <a class="link" :href="state.booking.icsUrl">Add to calendar</a>
        <RouterLink class="link" :to="manageLink(state.booking)">Change time</RouterLink>
      </div>
    </template>

    <template v-else>
      <div class="icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 11h18" />
        </svg>
      </div>
      <div class="body">
        <div class="label">Author Nation LIVE · Booth A7</div>
        <div class="title">Book your free editor consult</div>
        <p>
          Your roast is done. Now talk it through with a Twig editor in person. 15 minutes, any day Tue Nov 10 to Fri
          Nov 13.
        </p>
        <div class="picks">
          <RouterLink v-for="t in nextTimes" :key="t.startsAt" class="chip" :to="bookLink(t.startsAt)">
            {{ shortSlotLabel(t.startsAt) }}
          </RouterLink>
          <RouterLink class="btn" :to="bookLink()">See all times</RouterLink>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { consultApi, shortSlotLabel, type ConsultBooking, type ConsultMe, type ConsultTime } from '../lib/consults';

const props = defineProps<{ projectId: string }>();

const state = ref<ConsultMe | null>(null);
const nextTimes = ref<ConsultTime[]>([]);

const bookLink = (time?: string) => ({
  path: '/author-nation/book',
  query: { project: props.projectId, ...(time && { time }) },
});
const manageLink = (b: ConsultBooking) => ({ path: `/author-nation/booking/${b.id}`, query: { token: b.token } });

onMounted(async () => {
  try {
    const me = await consultApi.me(props.projectId);
    // Only roasts that came through the Author Nation link get the card
    if (me.projectId !== props.projectId) return;
    if (!me.booking) {
      const { times } = await consultApi.times();
      nextTimes.value = times.filter(t => t.open > 0).slice(0, 3);
    }
    state.value = me;
  } catch {
    // The card is an extra; never let it break the results page
  }
});
</script>

<style scoped>
.consult-card {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin: 16px 24px 0;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid var(--color-border-primary);
  border-left: 4px solid #1a5f8a;
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-sm);
}
.consult-card.booked {
  align-items: center;
  border-left-color: var(--color-success);
}
.icon {
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: #f0f7fc;
  color: #1a5f8a;
}
.icon.done {
  background: #f2faf5;
  color: var(--color-success);
}
.body {
  flex: 1;
  min-width: 0;
}
.label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1a5f8a;
}
.title {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 2px 0 4px;
}
.booked .title {
  font-size: 15px;
  margin-bottom: 0;
}
p {
  margin: 0 0 12px;
  color: var(--color-text-secondary);
}
.picks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.chip {
  padding: 7px 12px;
  border: 1px solid var(--color-border-secondary);
  border-radius: 999px;
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-primary);
  text-decoration: none;
  background: #fff;
}
.chip:hover {
  border-color: var(--color-accent);
  background: var(--color-accent-light);
}
.btn {
  padding: 7px 16px;
  border-radius: var(--border-radius);
  background: var(--color-accent);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.btn:hover {
  background: var(--color-accent-hover);
}
.actions {
  display: flex;
  gap: 16px;
  flex: none;
}
.link {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-accent-hover);
  text-decoration: none;
}
.link:hover {
  text-decoration: underline;
}
@media (max-width: 768px) {
  .consult-card {
    margin: 12px 12px 0;
    flex-wrap: wrap;
  }
  .actions {
    width: 100%;
    padding-left: 52px;
  }
}
</style>
